import { useCallback, useEffect, useState } from "react"

import type { ApiProblem } from "../../api/problem"
import { isApiProblem, transportProblem } from "../../api/problem"

/**
 * Загрузка справочника.
 *
 * ТО ЖЕ ПРАВИЛО, ЧТО У ЖУРНАЛА (screens/journal/use-journal-day.ts), и оно здесь
 * не ради единообразия: при сбое ПРЕЖНИЕ ДАННЫЕ НЕ СТИРАЮТСЯ. Чтение работает всегда,
 * изменения — только со связью (ТЗ §5.11). Список клиентов, пропавший с экрана из-за
 * обрыва Wi-Fi, — это ровно то, чего нельзя делать: администратор диктует по нему
 * номер в трубку прямо сейчас.
 *
 * `refreshing` отделён от `loading` по той же причине: перезагрузка уже показанного
 * списка не должна очищать экран (ТЗ §5.6 п.1).
 */

export interface Resource<T> {
  readonly data: T | null
  readonly problem: ApiProblem | null
  /** Первая загрузка: показывать каркас. */
  readonly loading: boolean
  /** Перезагрузка поверх показанных данных. */
  readonly refreshing: boolean
  /**
   * Когда данные приехали. Нужен подписи «показан список на 14:32» при обрыве связи:
   * показывать устаревшее без возраста — значит выдавать его за свежее.
   */
  readonly loadedAt: Date | null
  readonly reload: () => void
}

export function useResource<T>(
  load: ((signal: AbortSignal) => Promise<T>) | null,
  /** Ключ запроса строкой: объект параметров пересобирается на каждый рендер. */
  key: string | null,
): Resource<T> {
  const [data, setData] = useState<T | null>(null)
  const [loadedAt, setLoadedAt] = useState<Date | null>(null)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const [loading, setLoading] = useState(load !== null)
  const [refreshing, setRefreshing] = useState(false)
  const [attempt, setAttempt] = useState(0)

  const reload = useCallback(() => {
    setAttempt((value) => value + 1)
  }, [])

  useEffect(() => {
    if (load === null || key === null) {
      setLoading(false)
      return
    }

    const controller = new AbortController()
    setProblem(null)
    setData((previous) => {
      setLoading(previous === null)
      setRefreshing(previous !== null)
      return previous
    })

    const run = async (): Promise<void> => {
      try {
        const result = await load(controller.signal)
        if (controller.signal.aborted) return
        setData(result)
        setLoadedAt(new Date())
        setProblem(null)
      } catch (error) {
        // Отменённый запрос — не сбой: параметры сменились быстрее, чем доехал ответ.
        if (controller.signal.aborted) return
        setProblem(isApiProblem(error) ? error : transportProblem("unknown"))
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
          setRefreshing(false)
        }
      }
    }
    void run()

    return () => {
      controller.abort()
    }
    // load намеренно вне зависимостей: он пересоздаётся на каждый рендер,
    // а условием перезапроса служит key — ровно как в журнале.
  }, [key, attempt])

  return { data, problem, loading, refreshing, loadedAt, reload }
}
